import { INCURSION_SITES } from '@somnhold/content';
import type { IncursionNode } from '@somnhold/sim';
import { t } from '../i18n.ts';
import { h } from '../ui/dom.ts';
import { ICONS } from '../ui/icons.ts';

const NODE_ICON: Record<string, string> = { quarry: ICONS.stone, forest: ICONS.wood, crystal: ICONS.crystal, altar: ICONS.dust, rescue: ICONS.keep, lair: ICONS.skull, iron: ICONS.iron, fort: ICONS.hammer };

/** The shell: an ellipse around the bastion, which sits at its center. */
const CX = 500;
const CY = 530;
const RX = 362;
const RY = 404;

const f = (n: number): string => n.toFixed(1);

/** Points of an ellipse arc band (a marginal scute) between two angles, as an SVG path. */
const band = (inner: number, a0: number, a1: number): string => {
  const p = (a: number, k: number): string => `${f(CX + Math.sin(a) * RX * k)} ${f(CY - Math.cos(a) * RY * k)}`;
  return `M${p(a0, 1)} L${p(a1, 1)} L${p(a1, inner)} L${p(a0, inner)}Z`;
};

/** A hexagon-ish plate around a center. */
const plate = (cx: number, cy: number, r: number, squash = 1): string => {
  const pts: string[] = [];
  for (let i = 0; i < 6; i++) {
    const a = (Math.PI / 3) * i + Math.PI / 6;
    pts.push(`${f(cx + Math.cos(a) * r)},${f(cy + Math.sin(a) * r * squash)}`);
  }
  return pts.join(' ');
};

/** A scute with its growth rings and a lit upper edge. */
const scute = (cx: number, cy: number, r: number, squash: number): string =>
  `<polygon points="${plate(cx, cy, r, squash)}" fill="url(#scute)" stroke="#0a0c09" stroke-width="5" stroke-linejoin="round"/>` +
  `<polygon points="${plate(cx, cy, r * 0.74, squash)}" fill="none" stroke="#0a0c09" stroke-opacity=".38" stroke-width="2.5"/>` +
  `<polygon points="${plate(cx, cy, r * 0.48, squash)}" fill="url(#scuteIn)" stroke="#0a0c09" stroke-opacity=".3" stroke-width="2"/>` +
  `<polyline points="${plate(cx, cy, r - 5, squash).split(' ').slice(3, 6).join(' ')}" fill="none" stroke="#c7d0b4" stroke-opacity=".16" stroke-width="3" stroke-linecap="round"/>`;

/** A leg (or the head, or the tail) reaching out of the shell toward a place on the map. */
const reach = (tx: number, ty: number, w: number, claws: boolean): string => {
  const dx = tx - CX;
  const dy = ty - CY;
  const k = 0.82 / Math.hypot(dx / RX, dy / RY);
  const ax = CX + dx * k;
  const ay = CY + dy * k;
  const len = Math.hypot(tx - ax, ty - ay) + w * 1.25;
  const rot = (Math.atan2(dx, -dy) * 180) / Math.PI;
  const folds = [0.3, 0.52, 0.72].map((q) => `M${f(-w * 0.62)} ${f(-len * q)} q${f(w * 0.62)} ${f(w * 0.22)} ${f(w * 1.24)} 0`).join(' ');
  const scales = [0.4, 0.62, 0.82].map((q, i) => `<ellipse cx="${f((i % 2 ? -1 : 1) * w * 0.22)}" cy="${f(-len * q)}" rx="${f(w * 0.16)}" ry="${f(w * 0.11)}"/>`).join('');
  const nails = claws
    ? `<path d="M${f(-w * 0.5)} ${f(-len + w * 0.3)} l${f(-w * 0.18)} ${f(-w * 0.42)} M0 ${f(-len - w * 0.05)} l0 ${f(-w * 0.45)} M${f(w * 0.5)} ${f(-len + w * 0.3)} l${f(w * 0.18)} ${f(-w * 0.42)}" stroke="#c3cad6" stroke-width="7" stroke-linecap="round"/>`
    : '';
  return (
    `<g transform="translate(${f(ax)} ${f(ay)}) rotate(${f(rot)})">` +
    `<path d="M${f(-w)} 0 C${f(-w * 1.1)} ${f(-len * 0.55)} ${f(-w * 0.8)} ${f(-len)} 0 ${f(-len - w * 0.1)} C${f(w * 0.8)} ${f(-len)} ${f(w * 1.1)} ${f(-len * 0.55)} ${f(w)} 0Z" fill="url(#skin)" stroke="#0a0c09" stroke-width="6"/>` +
    `<path d="${folds}" stroke="#0a0c09" stroke-opacity=".45" stroke-width="3" fill="none"/>` +
    `<g fill="#7d8697" opacity=".35">${scales}</g>` +
    nails +
    `</g>`
  );
};

/** The sleeping turtle from above: head, feet and tail out of a plated, mossy shell, with the bastion on top. */
const body = (): string => {
  const marg: string[] = [];
  const n = 30;
  for (let i = 0; i < n; i++) {
    const a0 = (i / n) * Math.PI * 2 + 0.01;
    const a1 = ((i + 1) / n) * Math.PI * 2 - 0.01;
    marg.push(`<path d="${band(0.87, a0, a1)}" fill="url(#marg)" stroke="#0a0c09" stroke-width="3.5" stroke-linejoin="round"/>`);
  }
  const vert = [0, 1, 2, 3, 4].map((i) => scute(CX, CY - 272 + i * 136, 74, 0.82)).join('');
  const costal = [-1, 1].flatMap((sx) => [0, 1, 2, 3].map((i) => scute(CX + sx * 176, CY - 206 + i * 138, 84, 0.78))).join('');
  // Head: a broad wedge with closed eyes, up where the warlords' den waits.
  const head =
    `<g><path d="M398 210 C376 110 430 12 500 6 C570 12 624 110 602 210Z" fill="url(#skin)" stroke="#0a0c09" stroke-width="6"/>` +
    `<path d="M446 150 q54 20 108 0 M452 182 q48 14 96 0" stroke="#0a0c09" stroke-opacity=".4" stroke-width="3" fill="none"/>` +
    `<path d="M454 76 q16 9 30 0 M516 76 q16 9 30 0" stroke="#c9b8ff" stroke-width="5" fill="none" stroke-linecap="round"/>` +
    `<path d="M454 76 q16 9 30 0 M516 76 q16 9 30 0" stroke="#c9b8ff" stroke-opacity=".35" stroke-width="12" fill="none" stroke-linecap="round" filter="url(#blur4)"/>` +
    `<path d="M488 44 l12 -6 12 6" stroke="#0a0c09" stroke-opacity=".5" stroke-width="3" fill="none"/></g>`;
  return (
    `<ellipse cx="${CX}" cy="${CY + 24}" rx="${RX + 70}" ry="${RY + 70}" fill="#000" opacity=".5" filter="url(#soft)"/>` +
    reach(856, 307, 80, true) +
    reach(144, 307, 80, true) +
    reach(848, 765, 74, true) +
    reach(152, 765, 74, true) +
    reach(500, 990, 40, false) +
    head +
    `<ellipse cx="${CX}" cy="${CY + 16}" rx="${RX + 4}" ry="${RY + 6}" fill="#000" opacity=".55" filter="url(#blur4)"/>` +
    `<ellipse cx="${CX}" cy="${CY}" rx="${RX}" ry="${RY}" fill="url(#shell)" stroke="#0a0c09" stroke-width="9"/>` +
    marg.join('') +
    costal +
    vert +
    // Moss, lichen and old scars on the plates; a grain over all of it.
    `<g fill="#5f7a4e" opacity=".32" filter="url(#blur4)"><circle cx="360" cy="410" r="34"/><circle cx="650" cy="620" r="26"/><circle cx="452" cy="742" r="22"/><circle cx="626" cy="330" r="20"/><circle cx="300" cy="640" r="18"/></g>` +
    `<g fill="#8fae6e" opacity=".28"><circle cx="352" cy="404" r="5"/><circle cx="372" cy="420" r="3"/><circle cx="660" cy="612" r="4"/><circle cx="446" cy="748" r="3"/></g>` +
    `<path d="M606 420 l30 40 -12 32 M376 646 l-24 36 18 28 M560 780 l22 18" stroke="#0a0c09" stroke-opacity=".55" stroke-width="4" fill="none" stroke-linecap="round"/>` +
    `<ellipse cx="${CX}" cy="${CY}" rx="${RX}" ry="${RY}" filter="url(#grain)" opacity=".5"/>` +
    `<ellipse cx="${CX}" cy="${CY}" rx="${RX}" ry="${RY}" fill="url(#sheen)"/>` +
    `<ellipse cx="${CX}" cy="${CY}" rx="${RX - 6}" ry="${RY - 6}" fill="none" stroke="#d7dfc3" stroke-opacity=".08" stroke-width="3"/>`
  );
};

/** Depth lines around the titan, like the contours of an old chart, and a thin frame with corner marks. */
const chart = (): string => {
  const rings = [70, 150, 240]
    .map((d, i) => `<ellipse cx="${CX}" cy="${CY}" rx="${RX + d}" ry="${RY + d}" fill="none" stroke="#c9d3e6" stroke-opacity="${0.07 - i * 0.015}" stroke-width="2" stroke-dasharray="${i === 0 ? '1 0' : '3 12'}"/>`)
    .join('');
  const corner = (x: number, y: number, sx: number, sy: number): string => `<path d="M${x} ${y + sy * 46} V${y} H${x + sx * 46}" fill="none" stroke="#c9d3e6" stroke-opacity=".28" stroke-width="2.5"/><rect x="${x + sx * 10 - 4}" y="${y + sy * 10 - 4}" width="8" height="8" transform="rotate(45 ${x + sx * 10} ${y + sy * 10})" fill="#ff4f3a" opacity=".55"/>`;
  return (
    rings +
    `<rect x="12" y="12" width="976" height="976" rx="10" fill="none" stroke="#c9d3e6" stroke-opacity=".1" stroke-width="2"/>` +
    corner(22, 22, 1, 1) +
    corner(978, 22, -1, 1) +
    corner(22, 978, 1, -1) +
    corner(978, 978, -1, -1)
  );
};

const DEFS = `<defs>
<radialGradient id="abyss" cx="50%" cy="52%" r="72%"><stop offset="0" stop-color="#1d222b"/><stop offset=".65" stop-color="#11141a"/><stop offset="1" stop-color="#08090c"/></radialGradient>
<radialGradient id="shell" cx="44%" cy="34%" r="78%"><stop offset="0" stop-color="#54604a"/><stop offset=".65" stop-color="#2f3629"/><stop offset="1" stop-color="#1a1e17"/></radialGradient>
<linearGradient id="scute" x1="0" y1="0" x2="0" y2="1"><stop offset="0" stop-color="#66704f"/><stop offset="1" stop-color="#363d2b"/></linearGradient>
<linearGradient id="scuteIn" x1="0" y1="0" x2="0" y2="1"><stop offset="0" stop-color="#717c58"/><stop offset="1" stop-color="#454d36"/></linearGradient>
<linearGradient id="marg" x1="0" y1="0" x2="1" y2="1"><stop offset="0" stop-color="#454d3a"/><stop offset="1" stop-color="#252a20"/></linearGradient>
<linearGradient id="skin" x1="0" y1="0" x2="0" y2="1"><stop offset="0" stop-color="#5f6878"/><stop offset="1" stop-color="#363b47"/></linearGradient>
<radialGradient id="sheen" cx="36%" cy="24%" r="62%"><stop offset="0" stop-color="#eceef2" stop-opacity=".16"/><stop offset="1" stop-color="#eceef2" stop-opacity="0"/></radialGradient>
<radialGradient id="keepglow"><stop offset="0" stop-color="#ff4f3a" stop-opacity=".5"/><stop offset="1" stop-color="#ff4f3a" stop-opacity="0"/></radialGradient>
<filter id="soft" x="-20%" y="-20%" width="140%" height="140%"><feGaussianBlur stdDeviation="22"/></filter>
<filter id="blur4" x="-20%" y="-20%" width="140%" height="140%"><feGaussianBlur stdDeviation="4"/></filter>
<filter id="grain" x="0" y="0" width="100%" height="100%"><feTurbulence type="fractalNoise" baseFrequency=".8" numOctaves="2" seed="3"/><feColorMatrix values="0 0 0 0 .04  0 0 0 0 .05  0 0 0 0 .03  0 0 0 -2.2 1.25"/><feComposite in2="SourceGraphic" operator="in"/></filter>
<filter id="mist" x="0" y="0" width="100%" height="100%"><feTurbulence type="fractalNoise" baseFrequency=".012" numOctaves="3" seed="7"/><feColorMatrix values="0 0 0 0 .2  0 0 0 0 .22  0 0 0 0 .27  0 0 0 1.1 -.42"/></filter>
<filter id="hole"><feGaussianBlur stdDeviation="34"/></filter>
</defs>`;

/** A gently curved trail between two places (the bend alternates so crossings read as separate paths). */
const trail = (x1: number, y1: number, x2: number, y2: number, bend: number): string => {
  const mx = (x1 + x2) / 2;
  const my = (y1 + y2) / 2;
  const len = Math.hypot(x2 - x1, y2 - y1) || 1;
  const nx = (-(y2 - y1) / len) * bend;
  const ny = ((x2 - x1) / len) * bend;
  return `M${x1} ${y1} Q${f(mx + nx)} ${f(my + ny)} ${x2} ${y2}`;
};

export interface TitanMapOpts {
  nodes: IncursionNode[];
  /** Who opened each place (player id), and players' colors. */
  openedBy: Record<string, string>;
  colorOf: (pid: string) => string | null;
  /** A player's name (the dot on a place says who opened it). */
  nameOf: (pid: string) => string | null;
  /** Places opened since this player last looked: they clear with an animation. */
  fresh: Set<number>;
  ranger: boolean;
  /** Level II at a place: 0 not yet, 1 open to play, 2 already won. */
  /** 1: level II open, 2: won at level II, 3: level III open, 4: won at level III. */
  tierOf?: (id: number) => 0 | 1 | 2 | 3 | 4;
  onPick: (id: number) => void;
}

/** The incursion map: the titan under a fog that lifts around every place the group has opened. */
export const titanMap = (o: TitanMapOpts): { el: HTMLElement; pick: (id: number) => void } => {
  const open = new Set(o.nodes.map((n) => n.id));
  const byId = new Map(INCURSION_SITES.map((x) => [x.id, x]));
  // Trails: worn paths between open places, faint dots into the fog where the next place waits.
  const roads: string[] = [];
  const frontier = new Set<number>();
  for (const s of INCURSION_SITES) {
    for (const j of s.next) {
      const tgt = byId.get(j);
      if (!tgt || j < s.id) continue;
      const a = open.has(s.id);
      const b = open.has(j);
      if (!a && !b) continue;
      if (a !== b) frontier.add(a ? j : s.id);
      const d = trail(s.x, s.y, tgt.x, tgt.y, (s.id + j) % 2 ? 16 : -16);
      roads.push(a && b ? `<path d="${d}" class="trail-bed"/><path d="${d}" class="trail"/>` : `<path d="${d}" class="trail far"/>`);
    }
  }
  const holes = [
    `<circle cx="${CX}" cy="${CY}" r="150" fill="#000"/>`,
    ...o.nodes.map((n) => `<circle cx="${n.x}" cy="${n.y}" r="150" fill="#000" class="${o.fresh.has(n.id) ? 'clearing' : ''}"/>`),
  ].join('');
  const svg =
    `<svg viewBox="0 0 1000 1000" class="titan-map" aria-hidden="true">${DEFS}` +
    `<rect width="1000" height="1000" fill="url(#abyss)"/>` +
    `<rect width="1000" height="1000" filter="url(#mist)" opacity=".55"/>` +
    chart() +
    body() +
    `<circle cx="${CX}" cy="${CY}" r="130" fill="url(#keepglow)"/>` +
    `<g class="roads">${roads.join('')}</g>` +
    `<mask id="fogmask"><rect width="1000" height="1000" fill="#fff"/><g filter="url(#hole)">${holes}</g></mask>` +
    `<g mask="url(#fogmask)" class="fog"><rect width="1000" height="1000" fill="#0b0d11" opacity=".66"/><rect width="1000" height="1000" filter="url(#mist)"/></g>` +
    // The bastion: a small keep on a red plinth at the heart of the shell.
    `<g transform="translate(${CX} ${CY})"><polygon points="${plate(0, 0, 44, 0.86)}" fill="#1a1416" stroke="#ff4f3a" stroke-opacity=".7" stroke-width="3"/>` +
    `<path d="M-22 22v-32l-10 0v-14h10v-8h12v8h20v-8h12v8h10v14h-10v32z" fill="#eceef2" stroke="#0a0c09" stroke-width="3"/><path d="M-6 22v-14h12v14z" fill="#ff4f3a"/></g>` +
    `</svg>`;
  const el = h('div.map-wrap', { html: svg });
  for (const id of frontier) {
    const s = byId.get(id);
    if (s) el.append(h('span.inc-unknown', { style: `left:${s.x / 10}%;top:${s.y / 10}%`, title: t('inc.unknown') }, '?'));
  }
  for (const n of o.nodes) {
    const dbl = n.double && o.ranger;
    const by = o.openedBy[String(n.id)];
    const color = by ? o.colorOf(by) : null;
    const who = by ? o.nameOf(by) : null;
    const label = who ? `${t(`site.${n.id}`)} · ${t('inc.openedBy', { name: who })}` : t(`site.${n.id}`);
    const b = h(
      `button.inc-node.k-${n.kind}.r${n.ring}${dbl ? '.double' : ''}${n.locked ? '.locked' : ''}${o.fresh.has(n.id) ? '.fresh' : ''}`,
      { style: `left:${n.x / 10}%;top:${n.y / 10}%`, 'data-node': n.id, 'aria-label': label, title: label },
      h('span.ni', { html: NODE_ICON[n.kind] as string }),
      n.locked ? h('span.nlock', { html: ICONS.lock }) : '',
      dbl ? h('span.nx2', {}, '×2') : '',
      color ? h('span.nby', { style: `background:${color}` }) : '',
      o.tierOf?.(n.id) ? tierMark(o.tierOf(n.id) as 1 | 2 | 3 | 4) : '',
    );
    b.addEventListener('click', () => o.onPick(n.id));
    el.append(b);
  }
  const pick = (id: number): void => {
    for (const x of el.querySelectorAll('[data-node]')) x.classList.toggle('picked', Number((x as HTMLElement).dataset.node) === id);
  };
  return { el, pick };
};

/** The level mark on a map node: II (Ascuas) or III (Obsidiana), lit once won there. */
const tierMark = (k: 1 | 2 | 3 | 4): HTMLElement =>
  h(`span.ntier${k >= 3 ? '.t3' : ''}${k === 2 || k === 4 ? '.won' : ''}`, { title: t(k === 1 ? 'ilevel.openTip' : k === 2 ? 'ilevel.wonTip' : k === 3 ? 'ilevel.openTip3' : 'ilevel.wonTip3') }, k >= 3 ? 'III' : 'II');
